import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, 
  ScrollText, 
  PieChart, 
  Bot, 
  ShieldAlert, 
  GraduationCap,
  ArrowRight,
  CheckCircle2,
  Globe,
  Smartphone,
  Download,
  Star,
  Users,
  BarChart3,
  Target,
  Zap,
  MessageCircle,
  Mail,
  MapPin,
  Phone,
  ChevronDown,
  ChevronUp,
  Play,
  Shield,
  Award,
  Clock,
  Wallet,
  LineChart
} from 'lucide-react';
import { setupInstallPrompt, installPWA, isPWA } from '../serviceWorkerRegistration';
import SubscribePremiumDialog from '../components/SubscribePremiumDialog';

export default function Landing() {
  const navigate = useNavigate();
  const { t, toggleLanguage, language } = useLanguage();
  const [canInstall, setCanInstall] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showPremium, setShowPremium] = useState(false);

  useEffect(() => {
    if (!isPWA()) {
      setupInstallPrompt((available) => setCanInstall(available));
    }
    // Auto-open the Premium modal if we came back from register with intent.
    if (new URLSearchParams(window.location.search).get('upgrade') === 'premium') {
      setShowPremium(true);
    }
  }, []);

  const handleInstall = async () => {
    await installPWA();
  };

  const features = [
    {
      icon: Bot,
      title_en: "AI Investment Coach",
      title_sw: "Mshauri wa AI",
      desc_en: "24/7 AI assistant trained on Tanzanian markets to answer your investment questions",
      desc_sw: "Msaidizi wa AI 24/7 aliyefunzwa kuhusu masoko ya Tanzania kujibu maswali yako"
    },
    {
      icon: BarChart3,
      title_en: "Portfolio Tracker",
      title_sw: "Mfuatiliaji wa Portfolio",
      desc_en: "Track DSE stocks, ETFs, bonds, and UTT funds all in one place",
      desc_sw: "Fuatilia hisa za DSE, ETF, hatifungani, na mifuko ya UTT mahali pamoja"
    },
    {
      icon: LineChart,
      title_en: "Investment Simulator",
      title_sw: "Kigeuzi cha Uwekezaji",
      desc_en: "See how your money can grow with different investment strategies",
      desc_sw: "Ona jinsi pesa zako zinavyoweza kukua na mikakati tofauti"
    },
    {
      icon: ShieldAlert,
      title_en: "Risk Assessment",
      title_sw: "Tathmini ya Hatari",
      desc_en: "Discover your investor profile and get personalized recommendations",
      desc_sw: "Gundua wasifu wako wa mwekezaji na upate mapendekezo ya kibinafsi"
    },
    {
      icon: Target,
      title_en: "Goal Planning",
      title_sw: "Mipango ya Malengo",
      desc_en: "Set financial goals and track your progress towards them",
      desc_sw: "Weka malengo ya kifedha na ufuatilie maendeleo yako"
    },
    {
      icon: GraduationCap,
      title_en: "Learning Center",
      title_sw: "Kituo cha Kujifunza",
      desc_en: "Step-by-step lessons on stocks, bonds, ETFs, and more",
      desc_sw: "Masomo ya hatua kwa hatua kuhusu hisa, hatifungani, ETF, na zaidi"
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title_en: "Create Free Account",
      title_sw: "Tengeneza Akaunti Bure",
      desc_en: "Sign up in 30 seconds with your email or phone number",
      desc_sw: "Jiandikishe kwa sekunde 30 na barua pepe au nambari ya simu"
    },
    {
      step: 2,
      title_en: "Take Risk Assessment",
      title_sw: "Fanya Tathmini ya Hatari",
      desc_en: "Answer simple questions to discover your investor profile",
      desc_sw: "Jibu maswali rahisi kugundua wasifu wako wa mwekezaji"
    },
    {
      step: 3,
      title_en: "Get Personalized Plan",
      title_sw: "Pata Mpango wa Kibinafsi",
      desc_en: "Receive AI-powered recommendations tailored to your goals",
      desc_sw: "Pokea mapendekezo ya AI yaliyoundwa kwa malengo yako"
    },
    {
      step: 4,
      title_en: "Start Investing",
      title_sw: "Anza Kuwekeza",
      desc_en: "Connect with licensed brokers and begin your journey",
      desc_sw: "Wasiliana na madalali wenye leseni na uanze safari yako"
    }
  ];

  const testimonials = [
    {
      name: "Amina M.",
      role_en: "Small Business Owner, Dar es Salaam",
      role_sw: "Mmiliki wa Biashara Ndogo, Dar es Salaam",
      quote_en: "Wekeza-Smart helped me understand UTT funds. Now I save for my children's education every month.",
      quote_sw: "Wekeza-Smart ilinisaidia kuelewa mifuko ya UTT. Sasa naweka akiba kwa elimu ya watoto wangu kila mwezi.",
      rating: 5
    },
    {
      name: "John K.",
      role_en: "Software Developer, Arusha",
      role_sw: "Mtengenezaji wa Programu, Arusha",
      quote_en: "The AI coach answers my questions at any time. It's like having a financial advisor in my pocket.",
      quote_sw: "Mshauri wa AI anajibu maswali yangu wakati wowote. Ni kama kuwa na mshauri wa kifedha mfukoni.",
      rating: 5
    },
    {
      name: "Grace L.",
      role_en: "Teacher, Mwanza",
      role_sw: "Mwalimu, Mwanza",
      quote_en: "I started with just TZS 50,000 in Umoja Fund. The app tracks everything for me automatically.",
      quote_sw: "Nilianza na TZS 50,000 tu katika Mfuko wa Umoja. Programu inafuatilia kila kitu kwa ajili yangu.",
      rating: 5
    }
  ];

  const pricingPlans = [
    {
      name_en: "Free",
      name_sw: "Bure",
      price: "0",
      period_en: "Forever",
      period_sw: "Milele",
      features_en: [
        "AI Coach (5 questions/day)",
        "Portfolio Tracker (3 investments)",
        "Investment Simulator",
        "Risk Assessment Quiz",
        "Basic Learning Modules"
      ],
      features_sw: [
        "Mshauri wa AI (maswali 5/siku)",
        "Mfuatiliaji wa Portfolio (uwekezaji 3)",
        "Kigeuzi cha Uwekezaji",
        "Jaribio la Tathmini ya Hatari",
        "Moduli za Msingi za Kujifunza"
      ],
      cta_en: "Start Free",
      cta_sw: "Anza Bure",
      popular: false
    },
    {
      name_en: "Premium",
      name_sw: "Premium",
      price: "15,000",
      period_en: "/month",
      period_sw: "/mwezi",
      features_en: [
        "Unlimited AI Coach",
        "Unlimited Portfolio Tracking",
        "Smart Rebalancing Advisor",
        "Portfolio Intelligence Score",
        "Goal-Based Planning",
        "Price Alerts via WhatsApp",
        "Advanced Analytics",
        "Priority Support"
      ],
      features_sw: [
        "Mshauri wa AI bila kikomo",
        "Ufuatiliaji wa Portfolio bila kikomo",
        "Mshauri wa Usawazishaji",
        "Alama ya Akili ya Portfolio",
        "Mipango ya Malengo",
        "Arifa za Bei kupitia WhatsApp",
        "Uchanganuzi wa Juu",
        "Msaada wa Kipaumbele"
      ],
      cta_en: "Go Premium",
      cta_sw: "Kuwa Premium",
      popular: true
    }
  ];

  const faqs = [
    {
      q_en: "Is Wekeza-Smart free to use?",
      q_sw: "Je, Wekeza-Smart ni bure kutumia?",
      a_en: "Yes! You can use our core features for free forever. Premium features are available for TZS 15,000/month.",
      a_sw: "Ndiyo! Unaweza kutumia huduma zetu za msingi bure milele. Huduma za Premium zinapatikana kwa TZS 15,000/mwezi."
    },
    {
      q_en: "How much money do I need to start investing?",
      q_sw: "Je, ninahitaji pesa ngapi kuanza kuwekeza?",
      a_en: "You can start with as little as TZS 10,000 for UTT funds or TZS 100,000 for DSE stocks. Our simulator helps you plan.",
      a_sw: "Unaweza kuanza na TZS 10,000 tu kwa mifuko ya UTT au TZS 100,000 kwa hisa za DSE. Kigeuzi chetu kinakusaidia kupanga."
    },
    {
      q_en: "Is my money safe?",
      q_sw: "Je, pesa zangu ziko salama?",
      a_en: "Wekeza-Smart is an educational platform. When you're ready to invest, we connect you with CMSA-licensed brokers and BOT-regulated products.",
      a_sw: "Wekeza-Smart ni jukwaa la elimu. Unapokuwa tayari kuwekeza, tunakuunganisha na madalali wenye leseni ya CMSA na bidhaa zinazodhibitiwa na BOT."
    },
    {
      q_en: "Can I use the app offline?",
      q_sw: "Je, ninaweza kutumia programu bila mtandao?",
      a_en: "Yes! Install the app on your phone and access your portfolio data even without internet. It syncs when you're back online.",
      a_sw: "Ndiyo! Sakinisha programu kwenye simu yako na ufikie data ya portfolio yako hata bila mtandao. Inasawazisha unapokuwa mtandaoni."
    },
    {
      q_en: "What investments can I track?",
      q_sw: "Je, ninaweza kufuatilia uwekezaji gani?",
      a_en: "You can track DSE stocks, UTT AMIS funds, ETFs (VIS-ETF, iTrust), Treasury Bills, and Government Bonds - all in one place.",
      a_sw: "Unaweza kufuatilia hisa za DSE, mifuko ya UTT AMIS, ETF (VIS-ETF, iTrust), Treasury Bills, na Hatifungani za Serikali - mahali pamoja."
    }
  ];

  const stats = [
    { value: "10,000+", label_en: "Active Users", label_sw: "Watumiaji Hai" },
    { value: "TZS 2B+", label_en: "Tracked Investments", label_sw: "Uwekezaji Unaofuatiliwa" },
    { value: "43", label_en: "Investment Options", label_sw: "Chaguo za Uwekezaji" },
    { value: "24/7", label_en: "AI Support", label_sw: "Msaada wa AI" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-xl text-foreground">Wekeza-Smart</span>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t('Features', 'Huduma')}
              </a>
              <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t('How It Works', 'Inavyofanya Kazi')}
              </a>
              <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t('Pricing', 'Bei')}
              </a>
              <a href="#faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t('FAQ', 'Maswali')}
              </a>
            </nav>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={toggleLanguage}
                className="flex items-center gap-2"
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4" />
                {language === 'en' ? 'SW' : 'EN'}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/login')}
                className="hidden sm:inline-flex"
                data-testid="login-btn"
              >
                {t('Login', 'Ingia')}
              </Button>
              <Button 
                size="sm"
                onClick={() => navigate('/register')}
                className="rounded-full"
                data-testid="get-started-btn"
              >
                {t('Get Started', 'Anza Sasa')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-pattern py-16 sm:py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="page-enter">
              <Badge variant="secondary" className="mb-6 px-4 py-2">
                <TrendingUp className="w-4 h-4 mr-2" />
                {t('Tanzania\'s #1 Investment App', 'Programu #1 ya Uwekezaji Tanzania')}
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground mb-4">
                {t('Jifunze Kuwekeza', 'Jifunze Kuwekeza')}
              </h1>
              <p className="text-2xl sm:text-3xl font-bold gradient-text mb-6">
                {t('Build Your Future Today', 'Jenga Mustakabali Wako Leo')}
              </p>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-xl">
                {t(
                  'Your AI-powered guide to DSE stocks, Treasury Bills, Bonds, ETFs, and UTT funds. Join thousands of Tanzanians growing their wealth.',
                  'Mwongozo wako wa AI kwa hisa za DSE, Treasury Bills, Hatifungani, ETF, na mifuko ya UTT. Jiunge na maelfu ya Watanzania wanaokua utajiri wao.'
                )}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/register')}
                  className="rounded-full text-base px-8 btn-press h-14"
                  data-testid="hero-cta-btn"
                >
                  {t('Start Learning Free', 'Anza Kujifunza Bure')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                {canInstall && (
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={handleInstall}
                    className="rounded-full text-base px-8 h-14"
                    data-testid="install-app-btn"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    {t('Install App', 'Sakinisha App')}
                  </Button>
                )}
              </div>
              
              <div className="flex flex-wrap gap-6">
                {[
                  { icon: CheckCircle2, text_en: "Free forever plan", text_sw: "Mpango wa bure milele" },
                  { icon: Shield, text_en: "CMSA & BOT regulated", text_sw: "Inashughulikiwa na CMSA & BOT" },
                  { icon: Smartphone, text_en: "Works offline", text_sw: "Inafanya kazi bila mtandao" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <item.icon className="w-4 h-4 text-primary" />
                    {t(item.text_en, item.text_sw)}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-3xl" />
              <div className="relative bg-card rounded-3xl shadow-2xl border border-border/50 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="font-bold text-foreground">Wekeza-Smart</p>
                    <p className="text-sm text-muted-foreground">{t('Your Portfolio', 'Portfolio Yako')}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-muted/50">
                    <p className="text-sm text-muted-foreground mb-1">{t('Total Value', 'Thamani Jumla')}</p>
                    <p className="text-3xl font-bold text-foreground">TZS 2.5M</p>
                    <p className="text-sm text-green-500 flex items-center gap-1 mt-1">
                      <TrendingUp className="w-4 h-4" /> +12.5% {t('this month', 'mwezi huu')}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {['CRDB', 'Umoja Fund', 'VIS-ETF', 'T-Bills'].map((item, i) => (
                      <div key={i} className="p-3 rounded-lg bg-muted/30 text-center">
                        <p className="text-xs text-muted-foreground">{item}</p>
                        <p className="font-semibold text-foreground text-sm">+{(Math.random() * 15 + 5).toFixed(1)}%</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-border/50 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl sm:text-4xl font-bold text-primary mb-2">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{t(stat.label_en, stat.label_sw)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">{t('Features', 'Huduma')}</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
              {t('Everything You Need to Invest Smart', 'Kila Unachohitaji Kuwekeza kwa Busara')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t(
                'From AI coaching to portfolio tracking, we have the tools to help you grow your wealth the Tanzanian way.',
                'Kutoka ushauri wa AI hadi ufuatiliaji wa portfolio, tuna zana za kukusaidia kukuza utajiri wako kwa njia ya Kitanzania.'
              )}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="card-hover border-border/50 bg-card group">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg text-foreground mb-2">
                    {t(feature.title_en, feature.title_sw)}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t(feature.desc_en, feature.desc_sw)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 sm:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">{t('How It Works', 'Inavyofanya Kazi')}</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
              {t('Start Investing in 4 Simple Steps', 'Anza Kuwekeza kwa Hatua 4 Rahisi')}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-semibold text-lg text-foreground mb-2">
                    {t(step.title_en, step.title_sw)}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t(step.desc_en, step.desc_sw)}
                  </p>
                </div>
                {i < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-border" />
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              className="rounded-full px-8"
            >
              {t('Get Started Now', 'Anza Sasa')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">{t('Testimonials', 'Ushuhuda')}</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
              {t('What Our Users Say', 'Watumiaji Wetu Wanasema Nini')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <Card key={i} className="border-border/50">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, j) => (
                      <Star key={j} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-foreground mb-4 italic">
                    "{t(testimonial.quote_en, testimonial.quote_sw)}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="font-semibold text-primary">{testimonial.name[0]}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground text-sm">{testimonial.name}</p>
                      <p className="text-xs text-muted-foreground">{t(testimonial.role_en, testimonial.role_sw)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">{t('Pricing', 'Bei')}</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
              {t('Simple, Transparent Pricing', 'Bei Rahisi na Wazi')}
            </h2>
            <p className="text-muted-foreground">
              {t('Start free, upgrade when you\'re ready', 'Anza bure, panda daraja unapokuwa tayari')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <Card key={i} className={`border-2 ${plan.popular ? 'border-primary shadow-lg' : 'border-border/50'} relative`}>
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                    {t('Most Popular', 'Maarufu Zaidi')}
                  </Badge>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {t(plan.name_en, plan.name_sw)}
                  </h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-foreground">TZS {plan.price}</span>
                    <span className="text-muted-foreground">{t(plan.period_en, plan.period_sw)}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {(language === 'en' ? plan.features_en : plan.features_sw).map((feature, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm">
                        <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full rounded-full ${plan.popular ? '' : 'variant-outline'}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => plan.popular ? setShowPremium(true) : navigate('/register')}
                    data-testid={plan.popular ? 'pricing-go-premium-btn' : 'pricing-start-free-btn'}
                  >
                    {t(plan.cta_en, plan.cta_sw)}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">FAQ</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
              {t('Frequently Asked Questions', 'Maswali Yanayoulizwa Mara kwa Mara')}
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <Card key={i} className="border-border/50">
                <CardContent className="p-0">
                  <button
                    className="w-full p-6 text-left flex items-center justify-between"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-semibold text-foreground pr-4">
                      {t(faq.q_en, faq.q_sw)}
                    </span>
                    {openFaq === i ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
                    )}
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-6 pt-0">
                      <p className="text-muted-foreground">
                        {t(faq.a_en, faq.a_sw)}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Install App CTA */}
      {canInstall && (
        <section className="py-12 bg-primary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-primary-foreground">
                <Smartphone className="w-12 h-12" />
                <div>
                  <h3 className="text-xl font-bold">
                    {t('Install Wekeza-Smart on Your Phone', 'Sakinisha Wekeza-Smart kwenye Simu Yako')}
                  </h3>
                  <p className="text-primary-foreground/80">
                    {t('Quick access, works offline, no app store needed', 'Ufikiaji wa haraka, inafanya kazi bila mtandao, huna haja ya duka la app')}
                  </p>
                </div>
              </div>
              <Button 
                size="lg" 
                variant="secondary"
                onClick={handleInstall}
                className="rounded-full shrink-0"
              >
                <Download className="w-5 h-5 mr-2" />
                {t('Install Now', 'Sakinisha Sasa')}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <Badge variant="outline" className="mb-4">{t('Contact Us', 'Wasiliana Nasi')}</Badge>
              <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4">
                {t('Get in Touch', 'Wasiliana Nasi')}
              </h2>
              <p className="text-muted-foreground mb-8">
                {t(
                  'Have questions? Our team is here to help you on your investment journey.',
                  'Una maswali? Timu yetu iko hapa kukusaidia katika safari yako ya uwekezaji.'
                )}
              </p>
              <div className="space-y-4">
                <a 
                  href="mailto:info@wekeza-smart.co.tz" 
                  className="flex items-center gap-4 hover:opacity-80 transition-opacity"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('General Inquiries', 'Maswali ya Jumla')}</p>
                    <p className="font-medium text-foreground">info@wekeza-smart.co.tz</p>
                  </div>
                </a>
                <a 
                  href="mailto:support@wekeza-smart.co.tz" 
                  className="flex items-center gap-4 hover:opacity-80 transition-opacity"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('Customer Support', 'Huduma kwa Wateja')}</p>
                    <p className="font-medium text-foreground">support@wekeza-smart.co.tz</p>
                  </div>
                </a>
                <a 
                  href="https://wa.me/14155238886" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 hover:opacity-80 transition-opacity"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-[#25D366]" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('WhatsApp AI Coach', 'Mshauri wa AI WhatsApp')}</p>
                    <p className="font-medium text-foreground">+1 415 523 8886</p>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('Location', 'Mahali')}</p>
                    <p className="font-medium text-foreground">Dar es Salaam, Tanzania</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Card className="border-border/50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4">{t('Send us a message', 'Tutumie ujumbe')}</h3>
                  <form className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">{t('Name', 'Jina')}</label>
                      <input 
                        type="text" 
                        className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
                        placeholder={t('Your name', 'Jina lako')}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">{t('Email', 'Barua pepe')}</label>
                      <input 
                        type="email" 
                        className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
                        placeholder={t('Your email', 'Barua pepe yako')}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">{t('Message', 'Ujumbe')}</label>
                      <textarea 
                        className="w-full p-3 rounded-lg border border-border bg-background text-foreground min-h-[120px]"
                        placeholder={t('How can we help?', 'Tunaweza kusaidiaje?')}
                      />
                    </div>
                    <Button className="w-full rounded-full">
                      {t('Send Message', 'Tuma Ujumbe')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="font-bold text-xl text-foreground">Wekeza-Smart</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('Tanzania\'s AI-powered investment education platform.', 'Jukwaa la elimu ya uwekezaji la AI la Tanzania.')}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">{t('Product', 'Bidhaa')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground">{t('Features', 'Huduma')}</a></li>
                <li><a href="#pricing" className="hover:text-foreground">{t('Pricing', 'Bei')}</a></li>
                <li><a href="#faq" className="hover:text-foreground">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">{t('Learn', 'Jifunze')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><span className="hover:text-foreground cursor-pointer">{t('DSE Stocks', 'Hisa za DSE')}</span></li>
                <li><span className="hover:text-foreground cursor-pointer">{t('UTT Funds', 'Mifuko ya UTT')}</span></li>
                <li><span className="hover:text-foreground cursor-pointer">ETFs</span></li>
                <li><span className="hover:text-foreground cursor-pointer">{t('Bonds', 'Hatifungani')}</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">{t('Legal', 'Kisheria')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><span className="hover:text-foreground cursor-pointer">{t('Privacy Policy', 'Sera ya Faragha')}</span></li>
                <li><span className="hover:text-foreground cursor-pointer">{t('Terms of Service', 'Masharti ya Huduma')}</span></li>
                <li><span className="hover:text-foreground cursor-pointer">{t('Disclaimer', 'Kanusho')}</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center sm:text-left">
              © 2026 Wekeza-Smart. {t('All rights reserved.', 'Haki zote zimehifadhiwa.')}
            </p>
            <p className="text-xs text-muted-foreground text-center">
              {t(
                'Educational platform. Not financial advice. Products regulated by CMSA & BOT.',
                'Jukwaa la elimu. Si ushauri wa kifedha. Bidhaa zinazodhibitiwa na CMSA & BOT.'
              )}
            </p>
          </div>
        </div>
      </footer>

      <SubscribePremiumDialog open={showPremium} onOpenChange={setShowPremium} />
    </div>
  );
}
